import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/useAuthStore';
import {
  type Task,
  type TaskUpdate,
  type CreateTaskInput,
  type TaskFilters,
  createTask,
  getTasks,
  getTask,
  updateTask,
  completeTask,
  uncompleteTask,
  deleteTask,
  getSections,
  createSection,
  type CreateSectionInput,
  archiveSection,
  reorderSections,
  moveTask,
  getArchivedTasks,
  restoreTask,
  type ArchivedTaskFilters,
} from '../../services/tasks/taskService';
import { STATIC } from '../../lib/queryPresets';
import { applyMoveInCache } from '../../lib/tasks/taskMoveCache';

// ============================================================
// Query keys
// ============================================================

export const tasksKeys = {
  all: ['tasks'] as const,
  list: (filters?: TaskFilters) => ['tasks', 'list', filters ?? {}] as const,
  byDossier: (dossierId: string | null) => ['tasks', 'dossier', dossierId] as const,
  one: (taskId: string) => ['tasks', 'one', taskId] as const,
  sections: (dossierId: string | null) => ['tasks', 'sections', dossierId] as const,
};

// ============================================================
// Stable filter constants (avoid re-renders)
// ============================================================

export const TASK_FILTERS = {
  ACTIVE_ONLY: { completed: false } as TaskFilters,
  COMPLETED_ONLY: { completed: true } as TaskFilters,
  ALL: {} as TaskFilters,
} as const;

// ============================================================
// Hooks — Tasks
// ============================================================

export function useTasks(filters?: TaskFilters) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC, // useTasksRealtime couvre déjà → pas de polling doublon
    queryKey: tasksKeys.list(filters),
    queryFn: () => getTasks(filters),
    enabled: isInitialized && !!session,
  });
}

export function useTask(taskId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC,
    queryKey: tasksKeys.one(taskId ?? ''),
    queryFn: () => getTask(taskId!),
    enabled: isInitialized && !!session && !!taskId,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => createTask(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, updates }: { taskId: string; updates: TaskUpdate }) =>
      updateTask(taskId, updates),
    // Optimiste : le changement (échéance via drag calendrier, priorité/date
    // via badges inline) s'affiche immédiatement, la RPC confirme derrière.
    onMutate: async ({ taskId, updates }) => {
      await qc.cancelQueries({ queryKey: tasksKeys.all });
      const snapshots = [
        ...qc.getQueriesData<Task[]>({ queryKey: ['tasks', 'list'] }),
        ...qc.getQueriesData<Task[]>({ queryKey: ['tasks', 'dossier'] }),
      ];
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData(
          key,
          data.map((t) => (t.id === taskId ? { ...t, ...updates } : t)),
        );
      }
      return { snapshots };
    },
    onError: (_err, _params, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => completeTask(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
      qc.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

export function useUncompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => uncompleteTask(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
      qc.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => deleteTask(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}

// ============================================================
// Hooks — Sections
// ============================================================

export function useTaskSections(dossierId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC, // useTasksRealtime couvre déjà → pas de polling doublon
    queryKey: tasksKeys.sections(dossierId),
    queryFn: () => getSections(dossierId),
    enabled: isInitialized && !!session,
  });
}

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSectionInput) => createSection(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tasksKeys.sections(vars.dossier_id ?? null) });
    },
  });
}

export function useArchiveSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => archiveSection(sectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks', 'sections'] });
    },
  });
}

export function useReorderSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dossierId,
      sectionIds,
    }: {
      dossierId: string;
      sectionIds: string[];
    }) => reorderSections(dossierId, sectionIds),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tasksKeys.sections(vars.dossierId) });
    },
  });
}

export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      taskId: string;
      targetSectionId: string | null;
      beforeTaskId?: string | null;
    }) => moveTask(params),
    // Optimiste : la ligne se pose immédiatement au bon endroit (drag liste
    // et kanban), la RPC confirme derrière. Sans ça, l'élément réapparaît à
    // son ancienne position le temps du round-trip puis saute — flicker.
    onMutate: async (params) => {
      await qc.cancelQueries({ queryKey: tasksKeys.all });
      // Uniquement les caches de listes de tâches — pas les sections.
      const snapshots = [
        ...qc.getQueriesData<Task[]>({ queryKey: ['tasks', 'list'] }),
        ...qc.getQueriesData<Task[]>({ queryKey: ['tasks', 'dossier'] }),
      ];
      for (const [key, data] of snapshots) {
        if (data) qc.setQueryData(key, applyMoveInCache(data, params));
      }
      return { snapshots };
    },
    onError: (_err, _params, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}

// ============================================================
// Hooks — Archives
// ============================================================

export function useArchivedTasks(filters?: ArchivedTaskFilters) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...STATIC, // useTasksRealtime couvre déjà → pas de polling doublon
    queryKey: ['tasks', 'archived', filters ?? {}],
    queryFn: () => getArchivedTasks(filters),
    enabled: isInitialized && !!session,
  });
}

export function useRestoreTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => restoreTask(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tasksKeys.all });
      qc.invalidateQueries({ queryKey: ['tasks', 'archived'] });
    },
  });
}

// Re-export types for convenience
export type { Task };
export type { TaskSection, ArchivedTaskFilters } from '../../services/tasks/taskService';
